import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import ArtifactViewer from "./ArtifactViewer";
import { sanitizeArtifactContent } from "../utils/sanitizeArtifact";
import type { PipelineReviewStateResponse } from "../lib/types";
import type { Language } from "../i18n/translations";
import { t } from "../i18n/translations";

const MVP_CONTACT_URL = import.meta.env.VITE_MVP_CONTACT_URL || "mailto:hello@buzarchitect.ai";

const STAGES = [
  { key: "business_case", label: "Business Case", desc: "Market analysis, value proposition, and competitive positioning" },
  { key: "product_spec", label: "Product Specification", desc: "Feature requirements, user stories, and product roadmap" },
  { key: "architecture", label: "Technical Architecture", desc: "System design, technology stack, and infrastructure plan" },
  { key: "epic_plan", label: "Epic Plan", desc: "Implementation epics, milestones, and 90-day execution timeline" },
];

interface PipelineCompleteProps {
  projectId: string;
  reviewState: PipelineReviewStateResponse;
  language: Language;
}

export default function PipelineComplete({
  projectId,
  reviewState,
  language,
}: PipelineCompleteProps) {
  const [viewingStage, setViewingStage] = useState<string | null>(null);
  const [downloadingStage, setDownloadingStage] = useState<string | null>(null);

  useEffect(() => {
    api.logEvent(projectId, "pipeline_complete_viewed");
  }, [projectId]);

  function handleDownloadMd(stage: string, content: string) {
    const blob = new Blob([sanitizeArtifactContent(content)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${stage.replace("_", "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const handleDownloadPdf = useCallback(async (stage: string) => {
    setDownloadingStage(stage);
    try {
      await api.downloadPipelineStage(projectId, stage);
    } catch {
      // Fallback to markdown download
      const stageInfo = reviewState.stages[stage];
      if (stageInfo?.artifact_content) {
        handleDownloadMd(stage, stageInfo.artifact_content);
      }
    } finally {
      setDownloadingStage(null);
    }
  }, [projectId, reviewState]);

  const viewingContent = viewingStage
    ? reviewState.stages[viewingStage]?.artifact_content || ""
    : "";

  return (
    <div className="space-y-6">
      {/* Success header */}
      <div className="bg-slate-800/80 border border-green-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <svg className="w-7 h-7 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <h2 className="text-xl font-semibold text-white">
            {t("pipeline.complete", language)}
          </h2>
        </div>
        <p className="text-sm text-slate-400">
          All four stages of your business plan have been reviewed and approved.
        </p>
      </div>

      {/* Artifact cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {STAGES.map(({ key, label, desc }) => {
          const stageInfo = reviewState.stages[key];
          const hasContent = !!stageInfo?.artifact_content;

          return (
            <div
              key={key}
              className="bg-slate-900/60 border border-slate-700/40 rounded-xl p-4 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <h3 className="text-sm font-semibold text-white">{label}</h3>
              </div>
              <p className="text-xs text-slate-400 flex-1 mb-3">{desc}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewingStage(key)}
                  disabled={!hasContent}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg py-1.5 transition-colors"
                >
                  View
                </button>
                <button
                  onClick={() => hasContent && handleDownloadPdf(key)}
                  disabled={!hasContent || downloadingStage === key}
                  className="bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 text-sm font-medium rounded-lg px-3 py-1.5 transition-colors"
                >
                  {downloadingStage === key ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* MVP Build upsell */}
      <div className="bg-slate-800/80 border border-blue-500/20 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-white mb-2">
          {t("upsell.mvp.tagline", language)}
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          {t("upsell.mvp.desc", language)}
        </p>
        <button
          onClick={() => {
            api.logEvent(projectId, "upsell_tier_clicked", { tier: "mvp_build", source: "pipeline_complete" });
            window.open(MVP_CONTACT_URL, "_blank");
          }}
          className="border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 text-sm font-medium rounded-lg px-4 py-2 transition-colors"
        >
          {t("upsell.mvp.cta", language)}
        </button>
      </div>

      {/* Artifact viewer modal */}
      {viewingStage && viewingContent && (
        <ArtifactViewer
          stage={viewingStage}
          content={viewingContent}
          onClose={() => setViewingStage(null)}
          onDownload={() => handleDownloadPdf(viewingStage)}
        />
      )}
    </div>
  );
}
